import React from 'react';
import { Eye, ChefHat, Store } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchSurveyDetail } from '../../redux/actions/surveyActions';
import { useLanguage } from "../../context/LanguageContext";

const KioskSurveyList = ({
  canteen,
  surveys,
  loading,
  onViewSurveyDetail,
  onBack,
  selectedSurvey,
  surveyType = "kiosk"
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  if (!canteen) return null;

  const headerIcon =
    surveyType === "kitchen"
      ? <ChefHat className="text-purple-600 mr-2" />
      : <Store className="text-blue-600 mr-2" />;

  const headerText =
    surveyType === "kitchen"
      ? t.global.kitchen_surveys_title?.replace("{areaName}", canteen.area_name || canteen.kitchen_location_fulladdress || 'Kitchen') || `Kitchen Surveys – ${canteen.area_name || canteen.kitchen_location_fulladdress || 'Kitchen'}`
      : t.global.kiosk_surveys_title?.replace("{areaName}", canteen.area_name) || `Surveys – ${canteen.area_name}`;

  // Handle completing/resuming a survey
  const handleCompleteSurvey = async (survey) => {
    try {
      console.log("Fetching survey detail for survey ID:", survey.id);
      
      // Fetch the complete survey detail
      const response = await dispatch(fetchSurveyDetail(survey.id));
      
      // Extract API response safely
      const surveyDetail = response?.payload?.data || response?.data || response;
      
      console.log("✅ Survey Detail loaded:", surveyDetail);
      
      if (!surveyDetail) {
        throw new Error("No survey data received");
      }
      
      // Save to localStorage for persistence
      localStorage.setItem("selectedSurvey", JSON.stringify(surveyDetail));
      localStorage.setItem("selectedCanteen", JSON.stringify(canteen));
      localStorage.setItem("surveyType", surveyType);
      localStorage.setItem("surveyId", survey.id);
      
      // Navigate with full data
      navigate("/survey/sections", {
        state: {
          canteen: canteen,
          surveyId: survey.id,
          surveyDetail: surveyDetail,
          resume: true,
          surveyType: surveyType
        }
      });
      
    } catch (error) {
      console.error("❌ Error fetching survey detail:", error);
      alert(t.global.alert_server_error || "Failed to load survey. Please try again.");
    }
  };

  // Handle viewing completed survey
  const handleViewSurvey = async (survey) => {
    try {
      console.log("Viewing completed survey:", survey.id);
      
      const response = await dispatch(fetchSurveyDetail(survey.id));
      const surveyDetail = response?.payload?.data || response?.data || response;
      
      if (onViewSurveyDetail) {
        onViewSurveyDetail({ ...survey, detail: surveyDetail });
      }
      
    } catch (error) {
      console.error("Error fetching survey detail:", error);
      alert(t.global.alert_server_error || "Failed to load survey details. Please try again.");
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return t.global.unknown_date || 'Unknown date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="mt-8">
      {/* Header */}
      <div className="flex items-center mb-4">
        {headerIcon}
        <h3 className="text-xl font-bold text-gray-800">
          {headerText}
        </h3>
      </div>

      {/* Loader */}
      {loading ? (
        <div className="text-center p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : surveys?.length === 0 ? (
        <div className="text-center p-6 border border-dashed border-gray-300 rounded-lg">
          {surveyType === "kitchen" ? (
            <ChefHat className="mx-auto text-gray-400 mb-2" size={40} />
          ) : (
            <Store className="mx-auto text-gray-400 mb-2" size={40} />
          )}
          <p className="text-gray-500 text-lg">
            {t.global.no_surveys_found?.replace("{surveyType}", surveyType === "kitchen" ? t.global.survey_type_kitchen : t.global.survey_type_kiosk) || `No ${surveyType} surveys found for this canteen`}
          </p>
          <p className="text-gray-400 text-sm mt-1">
            {t.global.start_new_survey || "Start a new survey to begin data collection"}
          </p>
        </div>
      ) : (
        <>
          {/* Warning for incomplete surveys */}
          {surveys.some(s => !s.total_survey_completed) && (
            <div className="mb-3 p-2 bg-orange-100 text-orange-700 text-sm rounded">
              {t.global.incomplete_surveys_warning || "⚠ Some surveys are incomplete. Click \"Resume Survey\" to continue."}
            </div>
          )}

          {/* Survey List */}
          <div className="space-y-3">
            {surveys.map((survey) => {
              const isCompleted = survey.total_survey_completed === true || survey.total_survey_completed === 1;
              
              return (
                <div
                  key={survey.id}
                  className={`border p-4 rounded flex justify-between items-center transition ${
                    isCompleted
                      ? "border-gray-200 hover:bg-gray-50"
                      : "bg-orange-50 border-orange-200"
                  }`}
                >
                  {/* Left Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">
                        {t.global.survey_id?.replace("{id}", survey.id) || `${surveyType === "kitchen" ? "Kitchen" : "Kiosk"} Survey #${survey.id}`}
                      </p>
                      {!isCompleted && (
                        <span className="px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                          {t.global.survey_incomplete || "Incomplete"}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-500 mt-1">
                      {t.global.survey_created?.replace("{date}", formatDate(survey.created_at)) || `Created: ${formatDate(survey.created_at)}`}
                    </p>

                    <div className="mt-2">
                      <span
                        className={`text-xs inline-block px-2 py-1 rounded-full ${
                          isCompleted
                            ? "bg-green-100 text-green-800"
                            : "bg-orange-100 text-orange-800"
                        }`}
                      >
                        {isCompleted 
                          ? t.global.status_completed_badge || "✓ Completed" 
                          : t.global.status_pending_badge || "⏳ Pending"}
                      </span>
                    </div>

                    {!isCompleted && (
                      <div className="text-xs text-orange-600 mt-1">
                        {t.global.action_required || "⚡ Action Required - Please complete this survey"}
                      </div>
                    )}
                  </div>

                  {/* Right Button */}
                  {isCompleted ? (
                    <button
                      onClick={() => handleViewSurvey(survey)}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-2 transition"
                    >
                      <Eye size={16} />
                      {t.global.view_details || "View Details"}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleCompleteSurvey(survey)}
                      className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded flex items-center gap-2 transition"
                    >
                      <span>📝</span>
                      {t.global.resume_survey || "Resume Survey"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Back Button */}
      {selectedSurvey && (
        <button
          onClick={onBack}
          className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
        >
          {t.global.back_to_survey_list || "← Back to Survey List"}
        </button>
      )}
    </div>
  );
};

export default KioskSurveyList;